"use client";

import { useEffect, useMemo, useState } from "react";

type Study = {
  key: string;
  label: string;
  species: string;
  unit: string;
  url: string;
};

type Gene = {
  id: string;
  currentIds?: string[];
  name: string | null;
  product: string | null;
  studies: string[];
};

type Index = {
  generated: string;
  matchingRule: string;
  studies: Study[];
  genes: Gene[];
};

const copy = {
  zh: {
    search: "按作者特征编号、基因名称或产物搜索",
    placeholder: "例如 Tb927.6.4280、GAPDH 或 amastin",
    loading: "正在载入36,741个可追溯表达特征…",
    error: "基因索引暂时无法读取。",
    matches: "个匹配特征",
    studies: "可查询研究",
    open: "打开该研究的真实表达视图",
    download: "下载当前检索结果CSV",
    empty: "没有精确或文本匹配。本站不会推断同源基因或跨版本映射。",
    current: "已核验当前编号",
    boundary:
      "作者表达矩阵编号始终是主编号；只有在固定版本数据库快照中核验成功时才显示当前编号。不同技术、物种、株系和处理单位的数值不能直接合并或做差异表达。",
  },
  en: {
    search: "Search author feature ID, gene name or product",
    placeholder: "For example Tb927.6.4280, GAPDH or amastin",
    loading: "Loading 36,741 traceable expression features…",
    error: "The gene index is currently unavailable.",
    matches: "matching features",
    studies: "queryable studies",
    open: "Open the real expression view for this study",
    download: "Download current results CSV",
    empty:
      "No exact-ID or text match. TrypCell does not infer orthologues or version mappings here.",
    current: "Verified current ID",
    boundary:
      "The author-matrix feature remains primary; a current ID is shown only when verified in a pinned database snapshot. Values from different technologies, species, strains and processing units must not be pooled or used directly for differential expression.",
  },
};

function csvCell(value: string) {
  return `"${value.replaceAll('"', '""')}"`;
}

export function CrossStudyGeneSearch() {
  const [language, setLanguage] = useState<"zh" | "en">("zh");
  const [index, setIndex] = useState<Index | null>(null);
  const [query, setQuery] = useState("Tb927.6.4280");
  const [error, setError] = useState(false);

  useEffect(() => {
    const updateLanguage = (event: Event) =>
      setLanguage((event as CustomEvent<"zh" | "en">).detail);
    window.addEventListener("trypcell-language", updateLanguage);
    setLanguage(document.body.dataset.language === "en" ? "en" : "zh");
    return () =>
      window.removeEventListener("trypcell-language", updateLanguage);
  }, []);

  useEffect(() => {
    fetch("/data/cross-study-gene-index.json")
      .then((response) => {
        if (!response.ok) throw new Error("index");
        return response.json() as Promise<Index>;
      })
      .then(setIndex)
      .catch(() => setError(true));
  }, []);

  const results = useMemo(() => {
    if (!index) return [];
    const needle = query.trim().toLowerCase();
    if (!needle) return [];
    return index.genes
      .filter((gene) =>
        [gene.id, ...(gene.currentIds ?? []), gene.name, gene.product]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(needle),
      )
      .sort((left, right) => {
        const leftExact =
          left.id.toLowerCase() === needle ||
          left.currentIds?.some((id) => id.toLowerCase() === needle) ||
          left.name?.toLowerCase() === needle;
        const rightExact =
          right.id.toLowerCase() === needle ||
          right.currentIds?.some((id) => id.toLowerCase() === needle) ||
          right.name?.toLowerCase() === needle;
        return Number(rightExact) - Number(leftExact);
      })
      .slice(0, 40);
  }, [index, query]);
  const studyByKey = new Map(
    index?.studies.map((study) => [study.key, study]) ?? [],
  );
  const t = copy[language];

  const downloadResults = () => {
    if (!index) return;
    const rows = [
      [
        "author_gene_id",
        "verified_current_ids",
        "gene_name",
        "product",
        "study",
        "species",
        "unit",
        "url",
      ],
      ...results.flatMap((gene) =>
        gene.studies.map((key) => {
          const study = studyByKey.get(key);
          return [
            gene.id,
            (gene.currentIds ?? []).join("|"),
            gene.name ?? "",
            gene.product ?? "",
            study?.label ?? key,
            study?.species ?? "",
            study?.unit ?? "",
            study?.url.replace("{gene}", encodeURIComponent(gene.id)) ?? "",
          ];
        }),
      ),
    ];
    const anchor = document.createElement("a");
    anchor.href = URL.createObjectURL(
      new Blob(
        [`${rows.map((row) => row.map(csvCell).join(",")).join("\n")}\n`],
        { type: "text/csv" },
      ),
    );
    anchor.download = "TrypCell-cross-study-gene-search.csv";
    anchor.click();
    URL.revokeObjectURL(anchor.href);
  };

  if (error) return <div className="cross-gene-state error">{t.error}</div>;
  if (!index) return <div className="cross-gene-state">{t.loading}</div>;

  return (
    <div className="cross-gene-search">
      <div className="cross-gene-toolbar">
        <label>
          <span>{t.search}</span>
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t.placeholder}
          />
        </label>
        <div>
          <strong>{results.length}</strong>
          <span>{t.matches}</span>
          <button type="button" onClick={downloadResults}>
            {t.download}
          </button>
        </div>
      </div>
      <div className="cross-gene-boundary">
        <strong>EXACT AUTHOR FEATURE IDS</strong>
        <p>{t.boundary}</p>
      </div>
      <div className="cross-gene-results">
        {results.map((gene) => (
          <article key={gene.id}>
            <header>
              <div>
                <span>{gene.name || "UNNAMED FEATURE"}</span>
                <h2>{gene.id}</h2>
                {gene.currentIds?.some((id) => id !== gene.id) ? (
                  <small className="current-id">
                    {t.current}:{" "}
                    {gene.currentIds.filter((id) => id !== gene.id).join(", ")}
                  </small>
                ) : null}
                <p>{gene.product || "No matched product annotation"}</p>
              </div>
              <strong>
                {gene.studies.length} {t.studies}
              </strong>
            </header>
            <div>
              {gene.studies.map((key) => {
                const study = studyByKey.get(key);
                if (!study) return null;
                return (
                  <a
                    key={`${gene.id}-${key}`}
                    href={study.url.replace(
                      "{gene}",
                      encodeURIComponent(gene.id),
                    )}
                  >
                    <span>{study.label}</span>
                    <strong>{study.species}</strong>
                    <small>{study.unit}</small>
                    <b>{t.open} →</b>
                  </a>
                );
              })}
            </div>
          </article>
        ))}
        {!results.length ? <p className="cross-gene-empty">{t.empty}</p> : null}
      </div>
    </div>
  );
}
